import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export function TermsOfServiceEN() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-indigo-100">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center text-indigo-600 hover:text-indigo-700 mb-6 transition-colors duration-200"
        >
          <ArrowLeft className="mr-2 h-5 w-5" />
          Back to App
        </button>

        <article className="bg-white rounded-lg shadow-xl p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Terms of Service</h1>
          
          <div className="space-y-6 text-gray-700">
            <p className="text-sm text-gray-500">
              Last updated: {new Date().toLocaleDateString()}
            </p>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Company Information</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">
                  Simple Stickers is operated by <span className="font-semibold">Puretech s.r.o.</span>, 
                  a <span className="font-semibold">limited liability company</span> incorporated under the laws of <span className="font-semibold">Czechia</span>.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-2">Company Details:</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li><strong>Identification Number (IČO):</strong> 05503795</li>
                    <li><strong>VAT Status:</strong> Non-payer</li>
                    <li><strong>Office Address:</strong> Heydukova 1035, 54401 Dvůr Králové nad Labem, Czechia</li>
                    <li><strong>Email:</strong> support@simplestickers.app</li>
                    <li><strong>Website:</strong> <a href="https://www.simplestickers.app" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 hover:underline">www.simplestickers.app</a></li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Acceptance of Terms</h2>
              <p className="leading-relaxed">
                By accessing and using Simple Stickers ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. Description of Service</h2>
              <p className="leading-relaxed">
                Simple Stickers is a task and project management application that provides users with tools to organize sticky notes using a Kanban-style board system. The service includes both free and premium tiers with different feature sets and usage limits.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. User Accounts</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">
                  You are responsible for maintaining the confidentiality of your account and password and for restricting access to your computer. You agree to accept responsibility for all activities that occur under your account or password.
                </p>
                <p className="leading-relaxed">
                  You must provide accurate and complete information when creating your account and keep this information updated.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Subscription and Payment Terms</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">
                  <strong>Free Tier:</strong> Includes access to basic features with limitations on the number of sticky notes (50) and AI suggestions (10 per day).
                </p>
                <p className="leading-relaxed">
                  <strong>Premium Tier:</strong> Available for $48 per year, includes increased limits (1000 sticky notes, 100 AI suggestions per day) and additional features such as sticky note sharing capabilities.
                </p>
                <p className="leading-relaxed">
                  Payment processing is handled by Polar. Subscriptions automatically renew unless cancelled. You may cancel your subscription at any time through your account settings.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Acceptable Use</h2>
              <p className="leading-relaxed">
                You agree not to use the service to store, transmit, or distribute content that is illegal, harmful, threatening, abusive, harassing, defamatory, vulgar, obscene, or otherwise objectionable. You also agree not to interfere with the service or attempt to access accounts belonging to others.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Privacy and Personal Data Protection</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">
                  Your privacy is important to us. For details on how we collect, use, and protect your personal information, please see our <a href="/privacy-policy" className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium">Privacy Policy</a>.
                </p>
                <p className="leading-relaxed">
                  By using this service, you also agree to our Privacy Policy and the processing of your personal data in accordance with GDPR and applicable data protection laws.
                </p>
                <p className="leading-relaxed">
                  To exercise your rights under GDPR or for data protection inquiries, please contact us at <strong>support@simplestickers.app</strong>.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Service Availability and Support</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">
                  <strong>Service Availability:</strong> We strive for maximum service availability and reliable operation. In case of unplanned outages, we work to resolve them promptly. The service is provided "as is" without formal availability guarantees.
                </p>
                <p className="leading-relaxed">
                  <strong>Maintenance:</strong> To ensure security and stability, we may perform ongoing service maintenance. We aim to schedule planned maintenance during low-usage periods and provide advance notice whenever possible. In urgent cases (e.g., security patches), maintenance may occur without prior notice.
                </p>
                <p className="leading-relaxed">
                  <strong>Support:</strong> We offer technical support via email at support@simplestickers.app. We strive to respond as quickly as possible, however specific response times are not guaranteed.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. Refund Policy</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">
                  <strong>30-Day Money-Back Guarantee:</strong> If you are not satisfied with the premium service, you may request a full refund within 30 days of your initial purchase. After 30 days, all payments are final and non-refundable.
                </p>
                <p className="leading-relaxed">
                  <strong>Billing Errors:</strong> If you believe you have been incorrectly charged, contact us at support@simplestickers.app within 30 days of the charge.
                </p>
                <p className="leading-relaxed">
                  <strong>Refund Processing:</strong> Approved refunds will be processed within 10-14 business days to the original payment method. We reserve the right to approve or deny refund requests at our sole discretion.
                </p>
                <p className="text-sm text-gray-600">
                  Note: Subscription cancellations do not automatically result in refunds. You must explicitly request a refund within the 30-day period.
                </p>
              </div>
            </section>
            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">10. Age Restrictions and Eligibility</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">
                  <strong>Minimum Age:</strong> You must be at least 16 years old to use this service.
                </p>
                <p className="leading-relaxed">
                  <strong>Parental Consent:</strong> Users under 18 must have parental or guardian consent to use the service.
                </p>
                <p className="leading-relaxed">
                  <strong>Business Users:</strong> Business accounts require the user to have authority to bind the organization.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">11. Governing Law and Jurisdiction</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">
                  These Terms shall be governed by and construed in accordance with the laws of the Czech Republic, without regard to its conflict of law provisions.
                </p>
                <p className="leading-relaxed">
                  Any disputes arising from these Terms or your use of the Service shall be subject to the exclusive jurisdiction of the courts of the Czech Republic.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">12. Content Moderation</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">
                  We reserve the right to remove, modify, or refuse to display any content that violates these Terms or that we deem inappropriate, at our sole discretion.
                </p>
                <p className="leading-relaxed">
                  If you believe content should be reviewed, contact us at support@simplestickers.app.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">13. Intellectual Property</h2>
              <p className="leading-relaxed">
                The service and its original content, features, and functionality are owned by Simple Stickers and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">14. Limitation of Liability</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">
                  To the maximum extent permitted by applicable law, Simple Stickers shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your use of the service.
                </p>
                <p className="leading-relaxed">
                  This limitation of liability does not apply to damages caused by willful misconduct or gross negligence, or as otherwise prohibited by applicable law.
                </p>
                <p className="leading-relaxed">
                  Our total liability to you for any claims arising from your use of the service shall not exceed the amount you paid us in the 12 months preceding the claim, or €100, whichever is greater.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">15. Termination</h2>
              <p className="leading-relaxed">
                We may terminate or suspend your account and bar access to the service immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever, including without limitation if you breach the Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">16. Changes to Terms</h2>
              <p className="leading-relaxed">
                We reserve the right to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days notice prior to any new terms taking effect.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">17. Contact Information</h2>
              <div className="space-y-4">
                <p className="leading-relaxed">
                  If you have any questions about these Terms of Service, please contact us:
                </p>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Email:</strong> support@simplestickers.app
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    For all inquiries regarding support, legal matters, privacy, data protection, and business questions
                  </p>
                </div>
              </div>
            </section>
          </div>
        </article>
      </div>
    </div>
  );
}